import type { MetaFunction } from "@remix-run/node";
import { Link } from "@remix-run/react";
import { useState } from "react";
import { SEOLayout } from "~/components/seo/SEOLayout";
import { ContactEmailButton } from "~/components/contact-email";
import { getSeoMeta, getFaqPageJsonLd } from "~/utils/seo-meta";

const fontClass = "font-['Public Sans'] font-sans";

export const meta: MetaFunction = () =>
  getSeoMeta({
    title: "FAQ - Prizo",
    description:
      "Frequently asked questions about Prizo — answers about our Shopify gamification app, games, pricing, setup, and how it grows email lists and sales.",
    url: "/faq",
  });

export default function FAQ() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: "What is Prizo and how does it work?",
      answer:
        "Prizo is a Shopify gamification app that turns your store entry and post-purchase thank-you page into engaging mini-games. Shoppers play a game like spin the wheel or a scratch card to win discounts and rewards, which helps you capture emails, reduce cart abandonment, and turn one-time buyers into repeat customers. It installs from the Shopify App Store with no code.",
    },
    {
      question: "Which platform does Prizo work with?",
      answer:
        "Prizo is built specifically for Shopify. It installs as a native Shopify app and works on your storefront and the post-purchase thank-you page, with support for the Shopify Checkout UI where extensions are allowed. There are no theme edits and no developer required.",
    },
    {
      question: "What types of games are available?",
      answer:
        "Prizo includes spin the wheel, scratch card, pick a box, plinko, claw machine, basketball, hit the target, lucky dice, and break the ice — all in one app. Each game is quick, mobile-first, and fully on-brand, and you can run one or several and A/B test them.",
    },
    {
      question: "How do I set up rewards and prizes?",
      answer:
        "Rewards are set up in the Prizo dashboard. You can offer percentage or fixed-amount discounts, free shipping, free gifts, or custom rewards, and you control the win probability of each outcome so prizes stay exciting while protecting your margins. Rewards are issued automatically as native Shopify discount codes.",
    },
    {
      question: "Is Prizo mobile-friendly?",
      answer:
        "Yes. Every Prizo game is built mobile-first with touch-friendly controls and fast load times. Since the majority of Shopify traffic is on phones, the games are designed to feel smooth and responsive on every screen size.",
    },
    {
      question: "How much does Prizo cost?",
      answer:
        "Prizo is free to install from the Shopify App Store and offers a free trial, with paid plans as you scale. You can launch a game and start capturing emails and rewarding shoppers before committing to a paid plan. See the current plans on our pricing section or the App Store listing.",
    },
    {
      question: "Can I customize the games to match my brand?",
      answer:
        "Absolutely. You can customize colors, fonts, copy, and your logo, and tailor each game's look and feel to your brand. The goal is a surprise-and-delight moment that feels like part of your store, not a generic popup.",
    },
    {
      question: "How do I track game performance and analytics?",
      answer:
        "The Prizo dashboard shows plays, win rate, emails captured, reward redemptions, and the revenue and repeat purchases driven by your games — all in real time, so you can see ROI and optimize which games and rewards perform best.",
    },
    {
      question: "Can Prizo help me grow my email and SMS list?",
      answer:
        "Yes. Shoppers enter their email or phone number to play and claim a reward, so you capture engaged, opted-in subscribers in a moment of genuine interest — typically at much higher rates than a standard newsletter popup. Contacts sync to your marketing platform.",
    },
    {
      question: "How does Prizo reduce cart abandonment and increase sales?",
      answer:
        "Prizo gives shoppers a fun, rewarding reason to act in the moment — on store entry and on the thank-you page after checkout. The anticipation of playing and winning a real reward reduces hesitation, encourages completed checkouts, and seeds a next-order incentive that brings customers back, lifting repeat purchases and average order value.",
    },
    {
      question: "How quickly can I get started with Prizo?",
      answer:
        "Setup is fast — most stores are live within minutes. Install Prizo from the Shopify App Store, pick and brand a game, set up your rewards, and it appears automatically on your store and thank-you page. No coding or theme edits are needed.",
    },
    {
      question: "What kind of support do you provide?",
      answer:
        "We provide setup assistance, best-practice guidance, and responsive support — merchants consistently highlight our fast, hands-on help in their reviews. You can also book a live demo to see Prizo configured for your store.",
    },
    {
      question: "Can I integrate Prizo with my email marketing platform?",
      answer:
        "Yes. Prizo integrates with popular email and SMS platforms including Klaviyo and SendGrid, so the contacts you capture flow straight into your marketing automations and you can segment shoppers based on how they played and what they won.",
    },
    {
      question: "How do customers receive their rewards?",
      answer:
        "Rewards are delivered automatically. Prizo issues a native Shopify discount code the moment a shopper wins, and it can email the reward so they can redeem it on the current or a future order with no manual work on your side.",
    },
    {
      question: "Can I control how often shoppers play and win?",
      answer:
        "Yes. You control where games appear, how often a shopper can play, and the win probability of every reward. That lets you keep the experience exciting and fair while protecting your margins and reserving the best prizes for high-intent moments.",
    },
  ];

  return (
    <SEOLayout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getFaqPageJsonLd(faqs, "/faq")) }}
      />
      <div className="bg-[#ffffff] min-h-screen w-full">
        <div className="max-w-[1440px] mx-auto px-4 md:px-[120px] py-10 md:py-20">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h1
                className={`${fontClass} font-bold text-[32px] md:text-[48px] text-[#2c2c2c] mb-4`}
              >
                Frequently Asked Questions
              </h1>
              <p
                className={`${fontClass} text-[16px] md:text-[18px] text-[#6a6a6a] max-w-2xl mx-auto`}
              >
                Find answers to common questions about Prizo and how our gamification platform
                can boost your business.
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white border border-[#f0f0f0] rounded-2xl shadow-[0px_3px_8px_0px_rgba(44,44,44,0.06)] overflow-hidden"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className={`${fontClass} w-full px-6 md:px-8 py-6 text-left flex items-center justify-between hover:bg-[#f9f9f9] transition-colors`}
                  >
                    <h3 className="font-semibold text-[16px] md:text-[18px] text-[#2c2c2c] pr-4">
                      {faq.question}
                    </h3>
                    <div
                      className={`transform transition-transform duration-300 ${openFAQ === index ? "rotate-180" : ""}`}
                    >
                      <svg
                        className="w-5 h-5 text-[#696cff]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </button>

                  {/* Answer is always rendered in the DOM (so crawlers and the
                      FAQ schema see it) and only collapsed visually. */}
                  <div className={`px-6 md:px-8 ${openFAQ === index ? "pb-6" : "hidden"}`}>
                    <div className="border-t border-[#f0f0f0] pt-4">
                      <p
                        className={`${fontClass} text-[15px] md:text-[16px] text-[#4a4a4a] leading-[1.7]`}
                      >
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center bg-[#f9f9f9] rounded-2xl p-8 md:p-12">
              <h3
                className={`${fontClass} font-semibold text-[20px] md:text-[24px] text-[#2c2c2c] mb-4`}
              >
                Still have questions?
              </h3>
              <p
                className={`${fontClass} text-[15px] md:text-[16px] text-[#6a6a6a] mb-6 max-w-2xl mx-auto`}
              >
                Can't find the answer you're looking for? Our team is here to help you get the
                most out of Prizo.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <ContactEmailButton
                  className={`${fontClass} inline-flex items-center justify-center px-6 py-3 bg-[#696cff] text-white rounded-2xl font-medium hover:bg-[#5a5ce6] transition-colors`}
                >
                  Contact Support
                </ContactEmailButton>
                <Link
                  to="/"
                  className={`${fontClass} inline-flex items-center justify-center px-6 py-3 border border-[#696cff] text-[#696cff] rounded-2xl font-medium hover:bg-[#696cff] hover:text-white transition-colors`}
                >
                  See how Prizo works
                </Link>
              </div>
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/"
                className={`${fontClass} inline-flex items-center px-8 py-4 bg-[#696cff] text-white rounded-2xl font-medium hover:bg-[#5a5ce6] transition-colors`}
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </SEOLayout>
  );
}
